import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import PageLayout from "../../../components/layout/PageLayout/PageLayout";
import Card from "../../../components/common/Card";
import { FiInfo, FiLogOut, FiActivity, FiLayers } from "react-icons/fi";
import { BsKeyboard } from "react-icons/bs";
import { useModal } from "../../../context/ModalContext";
import "../../../styles/shared-pages.css";
import "./InstructionsPage.css";

export const KeyboardShortcutsProvider = ({
  children,
  isLoggedIn,
  onLogout,
  toggleSidebarCollapse,
}) => {
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  const { confirm } = useModal();

  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

      const target = e.target;
      const isInput = target.tagName === "Input" || target.tagName === "Textarea" || 
                      target.isContentEditable || target.closest(".form-control");

      if (isInput) return;

      const key = e.key.toLowerCase();

      switch (key) {
        case "s": {
          e.preventDefault();
          const searchInput = document.querySelector(".sidebar-search-input");
          const searchIconWrapper = document.querySelector(".sidebar-search-icon-wrapper");
          if (searchIconWrapper) searchIconWrapper.click();
          else if (searchInput) searchInput.focus();
          break;
        }
        case "o": {
          e.preventDefault();
          const menuOption = document.querySelector(".sidebar-search-input, .sidebar-search-icon-wrapper, .sidebar-link");
          if (menuOption) menuOption.focus();
          break;
        }
        case "w": {
          e.preventDefault();
          const walkButton = document.querySelector('[data-tour="restart-btn"]');
          if (walkButton) walkButton.click();
          break;
        }
        case "t":
          e.preventDefault();
          toggleTheme();
          break;
        case "c":
          e.preventDefault();
          if (toggleSidebarCollapse) toggleSidebarCollapse();
          break;
        case "l": {
          e.preventDefault();
          if (isLoggedIn && onLogout) {
            const yes = await confirm({
              title: "Logout Session?",
              message: "Are you sure you want to log out? Any unsaved progress may be lost.",
              confirmLabel: "Logout",
              variant: "danger",
              icon: <FiLogOut />,
            });
            if (yes) onLogout();
          }
          break;
        }
        case "a":
          e.preventDefault();
          navigate("/settings");
          break;
        case "p":
          e.preventDefault();
          navigate("/privacy-policies");
          break;
        case "r":
          e.preventDefault();
          navigate("/terms-of-services");
          break;
        case "b":
          e.preventDefault();
          if (isLoggedIn) navigate("/dashboard");
          else navigate("/login");
          break;
        case "i":
          e.preventDefault();
          navigate("/instructions");
          break;
        case "u":
          e.preventDefault();
          window.scrollBy({ top: -window.innerHeight * 0.8, behavior: "smooth" });
          break;
        case "d":
          e.preventDefault();
          window.scrollBy({ top: window.innerHeight * 0.8, behavior: "smooth" });
          break;
        case "x":
          e.preventDefault();
          document.activeElement?.blur();
          break;
        case "m":
          e.preventDefault();
          document.documentElement.style.zoom = (parseFloat(document.documentElement.style.zoom || 1) + 0.1).toString();
          break;
        case "n":
          e.preventDefault();
          document.documentElement.style.zoom = (parseFloat(document.documentElement.style.zoom || 1) - 0.1).toString();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, toggleTheme, isLoggedIn, onLogout, toggleSidebarCollapse, confirm]);

  return <>{children}</>;
};

const InstructionsPage = () => {
  return (
    <PageLayout
      title="User Guide"
      subtitle="Learn how to navigate and use the EduSync platform efficiently."
      eyebrow={<><FiInfo /> Instructions</>}
    >
      <div className="sp-cockpit-bg">
        <div className="sp-aurora sp-aurora-1"></div>
        <div className="sp-aurora sp-aurora-2"></div>
        <div className="sp-noise"></div>
      </div>

      <div className="sp-wrapper">
        <div className="instr-content-container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="instr-grid">
            <Card className="sp-glass instr-card">
              <div className="card-head">
                <div className="sp-ico-box"><FiLayers /></div>
                <h3 className="sp-page-title" style={{fontSize: '1.5rem', marginBottom: 0}}>General Navigation</h3>
              </div>
              <p>EduSync is designed to be fully accessible via both mouse and keyboard. You can quickly jump between sections using the sidebar or search for any page directly.</p>
              <ul className="instr-list">
                <li><strong>Tab</strong> — Move focus between elements</li>
                <li><strong>Enter / Space</strong> — Activate focused item</li>
                <li><strong>Esc</strong> — Close dialogs or modals</li>
              </ul>
            </Card>

            <Card className="sp-glass instr-card">
              <div className="card-head">
                <div className="sp-ico-box"><FiActivity /></div>
                <h3 className="sp-page-title" style={{fontSize: '1.5rem', marginBottom: 0}}>Visual Features</h3>
              </div>
              <p>The platform features modern visual effects like aurora backgrounds and smooth transitions for a premium experience.</p>
              <ul className="instr-list">
                <li><strong>Aurora Motion</strong> — Fluid background animations</li>
                <li><strong>Glassmorphism</strong> — Translucent UI elements</li>
                <li><strong>Smooth Scroll</strong> — Seamless page movement</li>
              </ul>
            </Card>

            <Card className="sp-glass instr-card full-width">
              <div className="card-head">
                <div className="sp-ico-box"><BsKeyboard /></div>
                <h3 className="sp-page-title" style={{fontSize: '1.5rem', marginBottom: 0}}>Keyboard Shortcuts</h3>
              </div>
              <p style={{ marginBottom: '20px' }}>Use these shortcuts to trigger actions instantly. (Hold <strong>Shift</strong> + Key)</p>
              <div className="shortcut-grid">
                <ShortcutItem keys={["Shift", "S"]} actionKey="s" description="Global Search" />
                <ShortcutItem keys={["Shift", "B"]} actionKey="b" description="Dashboard" />
                <ShortcutItem keys={["Shift", "T"]} actionKey="t" description="Toggle Theme" />
                <ShortcutItem keys={["Shift", "C"]} actionKey="c" description="Toggle Sidebar" />
                <ShortcutItem keys={["Shift", "A"]} actionKey="a" description="Settings" />
                <ShortcutItem keys={["Shift", "L"]} actionKey="l" description="Logout" />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const ShortcutItem = ({ keys, actionKey, description }) => {
  const triggerShortcut = () => {
    if (actionKey) {
      window.dispatchEvent(new KeyboardEvent("keydown", { key: actionKey, shiftKey: true }));
    }
  };

  return (
    <div className="shortcut-item sp-glass" onClick={triggerShortcut}>
      <span className="desc">{description}</span>
      <div className="keys">
        {keys.map((k, i) => (
          <React.Fragment key={i}>
            <kbd>{k}</kbd>
            {i < keys.length - 1 && <span className="plus">+</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default InstructionsPage;
